import { useEffect, useState } from "react";
import { initTracking } from "./lib/tracking";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { LoadingScreen } from "./components/LoadingScreen";
import { LoadingContext } from "./contexts/LoadingContext";
import { Suspense } from "react";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Totem from "./pages/Totem";
import { lazyWithRetry } from "./lib/lazyWithRetry";

const CompetitorAlternative = lazyWithRetry(() => import("./pages/seo/CompetitorAlternative"));
const BestVillasHuaHin = lazyWithRetry(() => import("./pages/seo/BestVillasHuaHin"));
const NewVillaDevelopments = lazyWithRetry(() => import("./pages/seo/NewVillaDevelopments"));
const HuaHinMarketGuide = lazyWithRetry(() => import("./pages/seo/HuaHinMarketGuide"));
const IntentGuidePage = lazyWithRetry(() => import("./pages/seo/IntentGuidePage"));
const HuaHinPoolVilla = lazyWithRetry(() => import("./pages/campaign/HuaHinPoolVilla"));
const ThankYou = lazyWithRetry(() => import("./pages/ThankYou"));
import { intentGuides } from "./data/intentGuides";

const queryClient = new QueryClient();

const AppContent = () => {
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Site-wide: capture ad attribution and fire a single PageView (no-op without a Pixel ID)
  useEffect(() => {
    initTracking();
  }, []);
  
  
  // Skip loading screen for totem, campaign and SEO pages
  const intentPaths = intentGuides.map((g) => g.path);
  const isSEOPage =
    location.pathname === "/totem" ||
    location.pathname === "/hua-hin-pool-villa" ||
    location.pathname === "/thank-you" ||
    location.pathname.startsWith("/hua-hin-villa") ||
    location.pathname.startsWith("/best-premium") ||
    location.pathname.startsWith("/new-villa") ||
    location.pathname.startsWith("/hua-hin-villa-market") ||
    intentPaths.includes(location.pathname);

  const handleLoadComplete = () => {
    setLoading(false);
    window.scrollTo(0, 0);
  };

  const triggerLoading = () => {
    setLoading(true);
  };

  const showLoading = loading && !isSEOPage;

  return (
    <LoadingContext.Provider value={{ triggerLoading, isLoading: loading }}>
      {showLoading && <LoadingScreen onLoadComplete={handleLoadComplete} />}
      <div className={`transition-opacity duration-500 ${showLoading ? "opacity-0" : "opacity-100"}`}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/totem" element={<Totem />} />
          <Route path="/hua-hin-pool-villa" element={<Suspense fallback={<div className="min-h-screen" />}><HuaHinPoolVilla /></Suspense>} />
          <Route path="/thank-you" element={<Suspense fallback={<div className="min-h-screen" />}><ThankYou /></Suspense>} />
          <Route path="/hua-hin-villa/:slug" element={<Suspense fallback={<div className="min-h-screen" />}><CompetitorAlternative /></Suspense>} />
          <Route path="/best-premium-pool-villas-hua-hin-2026" element={<Suspense fallback={<div className="min-h-screen" />}><BestVillasHuaHin /></Suspense>} />
          <Route path="/best-luxury-pool-villas-hua-hin-2026" element={<Navigate to="/best-premium-pool-villas-hua-hin-2026" replace />} />
          <Route path="/hua-hin-villa/botanica-luxury-alternative" element={<Navigate to="/hua-hin-villa/botanica-premium-alternative" replace />} />
          <Route path="/new-villa-developments-hua-hin" element={<Suspense fallback={<div className="min-h-screen" />}><NewVillaDevelopments /></Suspense>} />
          <Route path="/hua-hin-villa-market-2026-developer-guide" element={<Suspense fallback={<div className="min-h-screen" />}><HuaHinMarketGuide /></Suspense>} />
          {intentGuides.map((g) => (
            <Route
              key={g.slug}
              path={g.path}
              element={
                <Suspense fallback={<div className="min-h-screen" />}>
                  <IntentGuidePage />
                </Suspense>
              }
            />
          ))}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </LoadingContext.Provider>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
